import { Box } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FacetFilterRail } from "../../../components/UIToolkit/Filters/FacetFilterRail";
import { FilteredEmptyState } from "../../../components/UIToolkit/Filters/FilteredEmptyState";
import { FilterRailFrame } from "../../../components/UIToolkit/Filters/FilterRailFrame";
import type { FacetSpec } from "../../../components/UIToolkit/Filters/facetSections";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useFilterRailHandoff, useGridFacetFilters } from "../../../hooks/useFacetFilters";
import { pbjEmployeesGridKey } from "../../../services/LocalStorageService";
import { FONT_SIZE, STATUS } from "../../../theme/tokens";
import type { PbjAuditEmployeeRow } from "../../../utils/models";
import { useEditPbjEmployeeOverrideId, usePbjAuditEmployees } from "../hooks/usePbjAudit";
import type { WithBuilding } from "../network/buildingQueries";
import { usePbjNetwork } from "../network/PbjNetworkContext";
import { useBuildingGridParts, useWithBuildingFacet } from "../network/useBuildingGridParts";
import { dataTableSx } from "../PredictionTab/colors";
import { useFacetLabels } from "../pbjFacetLabels";
import { flagFacet, textFacet } from "../pbjFacets";
import { boldNameColumn } from "../pbjGridCells";
import { formatAuditDate } from "../pbjGridFormat";
import { pbjSortOptions, sortByOtherField } from "../pbjGridSorting";
import { displayedPbjId, EmployeeIdOverrideCell } from "./EmployeeIdOverrideCell";
import { ManageOverridesDialog } from "./ManageOverridesDialog";

type Props = {
	startDate: string;
	endDate: string;
};

const PAY_TYPE_LABEL_KEY: Record<PbjAuditEmployeeRow["payTypeCode"], string> = {
	"1": "pbj_model.audit.employees.pay_type.exempt",
	"2": "pbj_model.audit.employees.pay_type.non_exempt",
	"3": "pbj_model.audit.employees.pay_type.contract",
};

/** Unique grid id — fold in the array index so equal (pbjId, name) rows never collide. */
type EmployeeGridRow = WithBuilding<PbjAuditEmployeeRow> & { __rowId: string };

const NAVIGABLE_ROW_CLASS = "pbj-employees-navigable-row";

enum EmployeeReviewFlag {
	IdSetByHand = "id_set_by_hand",
	PayTypeSetByHand = "pay_type_set_by_hand",
	NoProfile = "no_profile",
}

const REVIEW_LABEL_KEY: Record<EmployeeReviewFlag, string> = {
	[EmployeeReviewFlag.IdSetByHand]: "pbj_model.audit.employees.filter.id_set_by_hand",
	[EmployeeReviewFlag.PayTypeSetByHand]: "pbj_model.staffing.filter.review.pay_type_set_by_hand",
	[EmployeeReviewFlag.NoProfile]: "pbj_model.audit.employees.filter.no_profile",
};

const COLUMN_FACET: Readonly<Record<string, string>> = { payTypeCode: "pay_type", terminationDate: "status" };

const TERMINATED = "terminated";
const ACTIVE = "active";

const isReviewFlag = (value: string): value is EmployeeReviewFlag => (Object.values(EmployeeReviewFlag) as string[]).includes(value);

// Layer a pointer cursor onto rows that deep-link to a nurse profile, on top of the shared PBJ table sx.
const dataTableSxForEmployees = (theme: Theme) => ({
	...dataTableSx(theme),
	[`& .MuiDataGrid-row.${NAVIGABLE_ROW_CLASS}`]: { cursor: "pointer" },
});

/**
 * Employees submission sheet — the PBJ employee roster for the window. Migrated to the canonical
 * SystemDataGrid (opaque pinned header, skeleton loader, internal scroll). Double-clicking a row that
 * links to a nurse profile deep-links there, mirroring the previous table's behavior.
 */
export const EmployeesTable: FC<Props> = ({ startDate, endDate }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	// Overrides are written to the header department, the only building editable here.
	const { homeDepartmentId } = usePbjNetwork();
	const { data, isLoading, isError } = usePbjAuditEmployees(startDate, endDate);
	const building = useBuildingGridParts<EmployeeGridRow>();
	const overrideIdMutation = useEditPbjEmployeeOverrideId();
	const [manageOverridesOpen, setManageOverridesOpen] = useState(false);

	// DEFAULT: no rows while loading, the same as the grid below; the overrides dialog then lists no one.
	const homeRows = useMemo(() => (data ?? []).filter((row) => row.buildingId === homeDepartmentId), [data, homeDepartmentId]);
	const rows = useMemo<EmployeeGridRow[]>(
		() => (data ?? []).map((row, index) => ({ ...row, __rowId: `${index}|${row.buildingId}|${row.pbjId}|${row.name}` })),
		[data],
	);

	// Mirrors the nurse deep-link shape used across the app (e.g. EmployeeChangelog, PtoGapViewSection).
	// load_only employees have no linked nurse profile; another building's profile opens from that building.
	const opensProfile = useCallback((row: EmployeeGridRow) => row.nurseId != null && building.canEditRow(row), [building.canEditRow]);
	const goToProfile = useCallback(
		(row: EmployeeGridRow) => {
			if (opensProfile(row)) navigate(`/${row.buildingId}/nurse/${row.nurseId}`);
		},
		[navigate, opensProfile],
	);

	const labels = useFacetLabels();
	const facets = useMemo<FacetSpec<EmployeeGridRow>[]>(
		() => [
			...building.facets,
			{
				dimension: flagFacet<EmployeeGridRow>("review", {
					[EmployeeReviewFlag.IdSetByHand]: (r) => r.pbjOverrideEmployeeId != null,
					[EmployeeReviewFlag.PayTypeSetByHand]: (r) => r.payTypeCodeOverride != null,
					[EmployeeReviewFlag.NoProfile]: (r) => r.nurseId == null,
				}),
				title: t("pbj_model.filter.needs_review"),
				getLabel: (key) => (isReviewFlag(key) ? t(REVIEW_LABEL_KEY[key]) : key),
				colorDot: STATUS.WARNING.main,
				order: Object.values(EmployeeReviewFlag),
			},
			{
				dimension: textFacet<EmployeeGridRow>("pay_type", (r) => r.payTypeCode),
				title: t("pbj_model.audit.employees.cols.pay_type"),
				getLabel: labels.payType,
			},
			{
				dimension: { id: "status", getKeys: (r: EmployeeGridRow) => [r.terminationDate == null ? ACTIVE : TERMINATED] },
				title: t("pbj_model.audit.employees.filter.status"),
				getLabel: (key) => (key === TERMINATED ? t("pbj_model.audit.employees.filter.terminated") : t("pbj_model.audit.employees.filter.active")),
				order: [ACTIVE, TERMINATED],
			},
		],
		[t, labels, building.facets],
	);
	const filters = useGridFacetFilters(rows, facets, building.sharedGroups);
	const railHandoff = useFilterRailHandoff(filters.highlight, useWithBuildingFacet(COLUMN_FACET), t("pbj_model.filter.in_rail"));

	const sortingOptions = useMemo(
		() => [
			sortByOtherField<EmployeeGridRow>("pbjId", t("pbj_model.audit.employees.cols.id"), displayedPbjId),
			...pbjSortOptions<EmployeeGridRow>([
				...building.sortFields,
				{ field: "name", label: t("pbj_model.audit.employees.cols.name") },
				{ field: "hireDate", label: t("pbj_model.audit.employees.cols.hire") },
				{ field: "terminationDate", label: t("pbj_model.audit.employees.cols.term") },
				{ field: "payTypeCode", label: t("pbj_model.audit.employees.cols.pay_type") },
			]),
		],
		[t, building.sortFields],
	);

	const columns: GridColDef<EmployeeGridRow>[] = useMemo(
		() => [
			...building.columns,
			{
				field: "pbjId",
				headerName: t("pbj_model.audit.employees.cols.id"),
				minWidth: 150,
				flex: 1,
				// Sort on the EFFECTIVE id (override ?? synced) so the order matches what the cell shows.
				valueGetter: (_value, row) => displayedPbjId(row),
				renderCell: (params: GridRenderCellParams<EmployeeGridRow>) => (
					<Box sx={{ fontFamily: "monospace", fontSize: FONT_SIZE.SECONDARY, color: "text.secondary", height: "100%", display: "flex", alignItems: "center" }}>
						{/* Another building's id is changed from that building's page. */}
						{building.canEditRow(params.row) ? (
							<EmployeeIdOverrideCell
								row={params.row}
								isSaving={overrideIdMutation.isPending}
								onSave={(pbjOverrideEmployeeId) => overrideIdMutation.mutateAsync({ pbjEmployeeId: params.row.pbjId, pbjOverrideEmployeeId })}
							/>
						) : (
							displayedPbjId(params.row)
						)}
					</Box>
				),
			},
			boldNameColumn<EmployeeGridRow>("name", t("pbj_model.audit.employees.cols.name")),
			{
				field: "hireDate",
				headerName: t("pbj_model.audit.employees.cols.hire"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value) => formatAuditDate(value),
			},
			{
				field: "terminationDate",
				headerName: t("pbj_model.audit.employees.cols.term"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value) => formatAuditDate(value),
			},
			{
				field: "payTypeCode",
				headerName: t("pbj_model.audit.employees.cols.pay_type"),
				minWidth: 160,
				flex: 1,
				valueGetter: (_value, row) => `${row.payTypeCode} — ${t(PAY_TYPE_LABEL_KEY[row.payTypeCode])}`,
			},
		],
		[t, overrideIdMutation.isPending, overrideIdMutation.mutateAsync, building.columns, building.canEditRow],
	);

	return (
		<>
			<FilterRailFrame
				flat
				dataTestId="pbj-model-audit-employees-frame"
				rail={
					<FacetFilterRail
						rows={rows}
						facets={facets}
						filters={filters}
						collapsed={railHandoff.railCollapsed}
						onCollapsedChange={railHandoff.setRailCollapsed}
						dataTestId="pbj-employees-filter"
					/>
				}
			>
				<SystemDataGrid<EmployeeGridRow>
					rows={filters.result.rows}
					canFilterByColumn={railHandoff.canFilterByColumn}
					onFilterByColumn={railHandoff.onFilterByColumn}
					filterByHint={railHandoff.filterByHint}
					sortingOptions={sortingOptions}
					showSortSummary
					localStorageKey={pbjEmployeesGridKey}
					exportOptions={{ fileName: t("pbj_model.audit.employees.export_file_name") }}
					columns={columns}
					getRowId={(r) => r.__rowId}
					// Match on the EFFECTIVE id so search stays WYSIWYG once an override is set.
					searchableColumns={[{ field: "pbjId", getValue: displayedPbjId }, "name", "buildingName"]}
					searchPlaceholder={t("pbj_model.audit.employees.search")}
					loading={isLoading}
					error={isError}
					errorMessage={t("pbj_model.audit.employees.failed")}
					emptyState={
						<FilteredEmptyState
							filtered={filters.activeCount > 0}
							onClearFilters={filters.clear}
							unfilteredState={<EmptyState title={t("pbj_model.audit.employees.empty")} dataTestId="pbj-model-audit-employees-empty" />}
							dataTestId="pbj-model-audit-employees-filtered-empty"
						/>
					}
					defaultPageSize={25}
					sx={(theme) => dataTableSxForEmployees(theme)}
					dataGridProps={{
						onRowDoubleClick: (params) => goToProfile(params.row as EmployeeGridRow),
						getRowClassName: (params) => (opensProfile(params.row as EmployeeGridRow) ? NAVIGABLE_ROW_CLASS : ""),
					}}
					trailingToolbarNodes={
						<SystemButton
							buttonVariant="secondary"
							size="small"
							noFullWidthMobile
							sx={{ ml: "auto" }}
							onClick={() => setManageOverridesOpen(true)}
							data-testid="pbj-employees-manage-overrides"
						>
							{t("pbj_model.override_manage.button")}
						</SystemButton>
					}
					dataTestId="pbj-model-audit-employees"
				/>
			</FilterRailFrame>
			<ManageOverridesDialog open={manageOverridesOpen} onClose={() => setManageOverridesOpen(false)} departmentId={homeDepartmentId} rows={homeRows} />
		</>
	);
};
